/**
 * @file Helper class for the international formatting of dates, times and currency
 * @author Tom Verschoore de la Houssaije.
 */
import { EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { SettingsService } from '../settings.service';
/** Service managing formatting of dates, times and currency */
var LocalisationService = /** @class */ (function () {
    function LocalisationService(translateService, settingsService) {
        this.translateService = translateService;
        this.settingsService = settingsService;
        this.onLanguageChanged = new EventEmitter();
    }
    /**
     * Converts a string array to an array of RegExps and strings, used by the masking
     * @static
     * @param {[string]} values
     * @returns {any[]}
     *
     */
    LocalisationService.toMaskArray = function (values) {
        var result = [];
        for (var _i = 0, values_1 = values; _i < values_1.length; _i++) {
            var value = values_1[_i];
            (value.startsWith('\\')) ? result.push(RegExp(value)) : result.push(value);
            // (value.length > 1) ? result.push(RegExp(value)) : result.push(value);
        }
        return result;
    };
    Object.defineProperty(LocalisationService.prototype, "currentLanguage", {
        /**
         * Gets the current Language object, if the current language isn't
         * set or the locale can not be found, the system will fall back on English.
         * @readonly
         * @type {Language}
         */
        get: function () {
            return this.language(this.translateService.currentLang);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalisationService.prototype, "duration", {
        get: function () {
            return this.currentLanguage.formats.duration;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalisationService.prototype, "date", {
        /**
         * Gets the DateFormat for the current language
         * @readonly
         * @type {DateFormat}
         */
        get: function () {
            return this.currentLanguage.formats.date;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalisationService.prototype, "shortDate", {
        /**
         * Gets the short DateFormat for the current language
         * @readonly
         * @type {DateFormat}
         */
        get: function () {
            return this.currentLanguage.formats.shortdate;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalisationService.prototype, "dayMonth", {
        /**
         * Gets the DateFormat displaying the day and abbreviated month for the current language
         * @readonly
         * @type {DateFormat}
         * @memberOf LocalisationService
         */
        get: function () {
            return this.currentLanguage.formats.daymonth;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalisationService.prototype, "time", {
        /**
         * Gets the time format based on the current language
         * @readonly
         * @type {DateFormat}
         */
        get: function () {
            return this.currentLanguage.formats.time;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalisationService.prototype, "dateTime", {
        /**
         * Gets the full DateTime format based on the current language
         * @readonly
         * @type {DateFormat}
         */
        get: function () {
            return this.currentLanguage.formats.datetime;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalisationService.prototype, "languages", {
        /**
         * Gets an array with the Language definitions from the settings
         * @readonly
         * @type {Language[]}
         */
        get: function () {
            return this.settingsService.settings.frontend.languages;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the language for the supplied locale, if the locale could
     * not be found, the default language will be returned (English)
     * @param {string} locale
     * @returns {Language}
     */
    LocalisationService.prototype.language = function (locale) {
        var ls = this.languages.filter(function (l) {
            return l.locale === locale;
        });
        if (ls.length === 0) {
            if (locale === this.settingsService.settings.frontend.default_language) {
                return this.languages[0];
            }
            else {
                return this.language(this.settingsService.settings.frontend.default_language);
            }
        }
        else {
            return ls[0];
        }
    };
    /**
     * Sets the language to the language with the supplied locale
     * @param {string} language
     */
    LocalisationService.prototype.setLanguage = function (locale) {
        this.translateService.use(locale);
        var language = this.language(locale);
        moment.locale(language.moment);
        this.onLanguageChanged.emit(language);
    };
    LocalisationService.prototype.calendarRelativeOptions = function () {
        return {
            sameDay: '[' + this.translateService.instant('APP.CORE.TODAY') + ']',
            nextDay: '[' + this.translateService.instant('APP.CORE.TOMORROW') + ']',
            lastDay: '[' + this.translateService.instant('APP.CORE.YESTERDAY') + ']',
            nextWeek: 'dddd',
            lastWeek: 'dddd',
            sameElse: 'dddd'
        };
    };
    return LocalisationService;
}());
export { LocalisationService };
